import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { signIn, supabase } from '../lib/supabase'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showReset, setShowReset] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetReason, setResetReason] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [resetMsg, setResetMsg] = useState(null)
  const navigate = useNavigate()

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    const { error } = await signIn(email, password)
    if (error) { setError(error.message); setLoading(false) }
    else navigate('/dashboard')
  }

  const handleResetRequest = async () => {
    if (!resetEmail) { setResetMsg({ type: 'error', text: 'Please enter your email address.' }); return }
    setResetLoading(true)
    // Find the profile by email
    const { data: profile } = await supabase.from('profiles').select('id').eq('email', resetEmail).single()
    if (!profile) { setResetMsg({ type: 'error', text: 'No account found with that email address.' }); setResetLoading(false); return }
    const { error } = await supabase.from('password_reset_requests').insert({
      user_id: profile.id,
      reason: resetReason || 'Forgot password',
      status: 'Pending',
    })
    if (error) { setResetMsg({ type: 'error', text: error.message }); setResetLoading(false); return }
    setResetMsg({ type: 'success', text: 'Reset request sent! An admin will review it and send you a reset link by email.' })
    setResetLoading(false)
    setResetEmail(''); setResetReason('')
  }

  return (
    <div className="login-page">
      <div className="login-box">
        {/* Logo */}
        <div className="login-logo">
          <img
            src="/logo.png"
            alt="Kobflo Greentek Ltd"
            style={{ width: '100%', maxWidth: 220, display: 'block', margin: '0 auto' }}
            onError={e => {
              e.target.style.display = 'none'
              document.getElementById('login-logo-fallback').style.display = 'block'
            }}
          />
          {/* Fallback if logo doesn't load */}
          <div id="login-logo-fallback" style={{ display: 'none', textAlign: 'center' }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.8rem', fontWeight: 900, color: 'var(--green-deep)' }}>
              Kobflo Greentek Ltd
            </div>
            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.75rem', color: 'var(--amber)', letterSpacing: '0.15em', textTransform: 'uppercase', marginTop: 4 }}>
              Premium Greenhouse Produce
            </div>
          </div>
        </div>

        <div style={{ height: 1, background: 'var(--border)', margin: '20px 0' }} />

        <div className="login-title">Farm Operations Hub</div>
        <div className="login-sub">Sign in with your account to continue</div>

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              className="form-input"
              type="email"
              placeholder="you@kobfloagri.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              className="form-input"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="alert alert-red" style={{ marginBottom: 14 }}>
              ⚠️ {error}
            </div>
          )}

          <button
            className="btn btn-primary btn-lg"
            type="submit"
            disabled={loading}
            style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}
          >
            {loading ? <><span className="spinner" /> Signing in...</> : '🌱 Sign In'}
          </button>
        </form>

        <div className="login-footer">
          Forgot your password? <button onClick={() => setShowReset(true)} style={{ background: 'none', border: 'none', color: 'var(--amber)', cursor: 'pointer', fontWeight: 700, textDecoration: 'underline', fontFamily: 'inherit', fontSize: 'inherit' }}>Request Reset</button><br />
          Don't have an account?<br />
          Contact <strong>Sharon</strong> or <strong>Edward</strong> for an invite link.
          <div style={{ marginTop: 10, fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', color: 'var(--amber)', letterSpacing: '0.1em' }}>
            app.kobfloagri.com
          </div>
        </div>

        {showReset && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', borderRadius: 12, padding: 28, maxWidth: 380, width: '90%' }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.2rem', fontWeight: 700, marginBottom: 8 }}>Request Password Reset</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 16 }}>Your request will be sent to admin for approval. Once approved, a reset link will be sent to your email.</div>
              {resetMsg && <div className={`alert ${resetMsg.type === 'success' ? 'alert-green' : 'alert-red'}`} style={{ marginBottom: 12 }}>{resetMsg.text}</div>}
              <div className="form-group"><label className="form-label">Your Email</label><input className="form-input" type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder="your@email.com" /></div>
              <div className="form-group"><label className="form-label">Reason (optional)</label><input className="form-input" value={resetReason} onChange={e => setResetReason(e.target.value)} placeholder="e.g. Forgot my password" /></div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button className="btn btn-ghost" onClick={() => { setShowReset(false); setResetMsg(null) }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleResetRequest} disabled={resetLoading}>{resetLoading ? 'Sending...' : '📧 Send Request'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
